import React, { useEffect, useState } from "react";
import { TopNav } from "@/components/TopNav";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { compressImageFile } from "@/lib/imageUtil";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Upload, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  unverified: { label: "Chưa xác thực", color: "text-white/60", bg: "bg-white/[0.03] border-white/10", icon: ShieldQuestion },
  pending: { label: "Đang chờ duyệt", color: "text-yellow-300", bg: "bg-yellow-400/10 border-yellow-400/30", icon: Loader2 },
  verified: { label: "Đã xác thực", color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30", icon: ShieldCheck },
  rejected: { label: "Bị từ chối", color: "text-red-300", bg: "bg-red-500/10 border-red-500/30", icon: ShieldAlert },
};

export default function Verify() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ real_name: "", phone: "", date_of_birth: "", cccd_number: "", cccd_front_image: "", cccd_back_image: "", selfie_image: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/me/verification").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const upload = (field) => async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const d = await compressImageFile(f, 1200, 0.85);
      setForm((s) => ({ ...s, [field]: d }));
      toast.success("Đã thêm ảnh");
    } catch { toast.error("Lỗi tải ảnh"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["real_name", "phone", "cccd_number", "cccd_front_image", "cccd_back_image", "selfie_image"]) {
      if (!form[k]) return toast.error("Vui lòng điền/tải đủ thông tin");
    }
    setBusy(true);
    try {
      await api.post("/me/verification", form);
      toast.success("Đã gửi yêu cầu xác thực — chờ admin duyệt");
      setForm({ real_name: "", phone: "", date_of_birth: "", cccd_number: "", cccd_front_image: "", cccd_back_image: "", selfie_image: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen bg-[#05050a]"><TopNav /><div className="p-10 text-white/40">Đang tải...</div></div>;

  const status = data.status;
  const meta = STATUS_META[status] || STATUS_META.unverified;
  const Icon = meta.icon;

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="text-emerald-300" />
          <h1 className="font-display font-bold text-3xl">Xác thực tài khoản</h1>
        </div>

        <div className={`rounded-xl border ${meta.bg} p-5 mb-6 flex items-center gap-3`} data-testid="verify-status">
          <Icon size={20} className={meta.color} />
          <div className="flex-1">
            <div className={`font-display font-semibold ${meta.color}`}>{meta.label}</div>
            {data.latest_request?.status === "rejected" && data.latest_request?.reject_reason && (
              <div className="text-xs text-red-300 mt-1">Lý do: {data.latest_request.reject_reason}</div>
            )}
            {status === "verified" && <div className="text-xs text-emerald-300 mt-1">Bạn đủ quyền tham gia rank chính thức, giải đấu và nhận thưởng lớn</div>}
          </div>
        </div>

        {(status === "unverified" || status === "rejected") && (
          <form onSubmit={submit} className="rounded-xl bg-[#0c0d14] border border-white/8 p-5 space-y-4" data-testid="verify-form">
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["Họ và tên thật *", "real_name", "text"],
                ["Số điện thoại *", "phone", "tel"],
                ["Ngày sinh", "date_of_birth", "date"],
                ["Số CCCD *", "cccd_number", "text"],
              ].map(([l, k, t]) => (
                <label key={k}>
                  <span className="text-[10px] uppercase tracking-wider text-white/50">{l}</span>
                  <input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`verify-${k}`}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
                </label>
              ))}
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              {[
                ["CCCD mặt trước *", "cccd_front_image"],
                ["CCCD mặt sau *", "cccd_back_image"],
                ["Ảnh selfie *", "selfie_image"],
              ].map(([l, k]) => (
                <div key={k} className="rounded-md border border-dashed border-white/20 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">{l}</div>
                  {form[k] ? (
                    <img src={form[k]} alt="" className="w-full h-32 object-cover rounded mb-2" />
                  ) : (
                    <div className="w-full h-32 grid place-items-center bg-white/5 rounded mb-2">
                      <Camera size={20} className="text-white/30" />
                    </div>
                  )}
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-white/5 hover:bg-white/10 border border-white/10">
                    <Upload size={11} /> {form[k] ? "Đổi" : "Tải"}
                    <input type="file" accept="image/*" onChange={upload(k)} className="hidden" data-testid={`upload-${k}`} />
                  </label>
                </div>
              ))}
            </div>

            <button type="submit" disabled={busy} data-testid="verify-submit" className="w-full py-3 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-emerald-400 to-cyan-500 text-black disabled:opacity-50 flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} Gửi yêu cầu xác thực
            </button>
          </form>
        )}

        {status === "pending" && (
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-8 text-center text-white/60">
            Yêu cầu của bạn đang được admin xem xét. Vui lòng đợi trong vài phút...
          </div>
        )}
      </div>
    </div>
  );
}
